import { MutableRefObject, useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import { isImageLikeObject } from '../utils/is-image-like-object';

type UseCachedActiveImageOptions = {
  canvas: MutableRefObject<fabric.Canvas | null> | null | undefined;
  enabled: boolean;
};

/**
 * Caches the most recently selected image-like fabric object on the canvas.
 *
 * Why: when a desktop inline tool button is clicked, focus can momentarily
 * leave the canvas and `getActiveObject()` returns `null`. Inline tools
 * (Zoom / Rotate / Layers) still need a target to operate on; this hook
 * exposes the last known good selection so tools can recover it.
 */
export const useCachedActiveImage = ({
  canvas,
  enabled,
}: UseCachedActiveImageOptions): MutableRefObject<fabric.Object | null> => {
  const cachedRef = useRef<fabric.Object | null>(null);

  useEffect(() => {
    if (!enabled) {
      cachedRef.current = null;
      return undefined;
    }

    const currentCanvas = canvas?.current;

    const cacheCurrent = () => {
      const active = currentCanvas?.getActiveObject();
      if (isImageLikeObject(active)) {
        cachedRef.current = active || null;
      }
    };

    cacheCurrent();

    if (!currentCanvas || typeof currentCanvas.on !== 'function') {
      return undefined;
    }

    const handleSelectionChanged = (opts: fabric.IEvent) => {
      const selectedFromOpts = (opts as { selected?: fabric.Object[] }).selected?.[0];
      const selected =
        selectedFromOpts || (opts.target as fabric.Object | undefined) || currentCanvas.getActiveObject();

      if (isImageLikeObject(selected)) {
        cachedRef.current = selected || null;
      }
    };

    currentCanvas.on('selection:created', handleSelectionChanged);
    currentCanvas.on('selection:updated', handleSelectionChanged);

    return () => {
      if (typeof currentCanvas.off !== 'function') {
        return;
      }
      currentCanvas.off('selection:created', handleSelectionChanged);
      currentCanvas.off('selection:updated', handleSelectionChanged);
    };
  }, [canvas, enabled]);

  return cachedRef;
};
